import { input, type QueryList, type SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChildren,
  output,
} from '@angular/core';

import type { UserBase } from '@core/account-types';
import type { Attachment } from '@core/attachments-types';
import type {
  BaseNote,
  NoteDeleteAttachmentEvent,
  NoteDiscardEvent,
  NoteEditEvent,
  NoteId,
  NoteLocalStorageKeyBuilder,
  NoteReplyChangesEvent,
  NoteReplyClickEvent,
  NoteReplyTypingStartEvent,
  NoteSave,
  NoteUploadAttachmentEvent,
  SavedNoteId,
} from '@core/notes-types';
import type { MentionElement } from '@core/prosemirror-legacy';

import type { NoteAvatarAuthor } from '../note-avatar/note-avatar.types';
import {
  type VisibilitySelector,
  type VisibilitySelectorInputs,
} from '../note-form/note-form.component';
import { NoteItemComponent } from '../note-item/note-item.component';
import { NoteTypingIndicatorMessageComponent } from '../note-typing-indicator-message/note-typing-indicator-message.component';

@Component({
  selector: 'rt-notes-list',
  templateUrl: './notes-list.component.html',
  styleUrls: ['./notes-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NoteTypingIndicatorMessageComponent, NoteItemComponent],
})
export class NotesListComponent<Note extends BaseNote<Note>> {
  public readonly notes = input<Note[]>(undefined!);
  public readonly currentlyTypedNotes = input<
    Array<{
      admin: UserBase;
      replyToId?: number | null;
    }>
  >([]);

  public readonly canDeleteAttachments = input<boolean>(false);
  public readonly canHighLevelDataAccess = input<boolean>(false);
  public readonly isReadOnly = input<boolean>(false);

  public readonly notesBeingSaved = input<NoteId[]>([]);
  public readonly notesBeingDeleted = input<NoteId[]>([]);
  public readonly notesBeingEdited = input<NoteId[]>([]);
  public readonly notesBeingRepliedTo = input<NoteId[]>([]);

  public readonly noteAttachmentsBeingUploaded = input<{
    [key: string]: string[];
  }>({});
  public readonly noteAttachmentsTemp = input<
    Record<
      string,
      Array<{
        uuid: string;
        attachment: Attachment;
      }>
    >
  >(undefined!);

  public readonly isOwnerFn = input.required<(note: Note) => boolean>();
  public readonly getOwnerFn = input.required<(note: Note) => NoteAvatarAuthor | null>();
  public readonly localStorageKeyBuilder = input.required<NoteLocalStorageKeyBuilder<Note>>();
  public readonly noteToMention = input.required<(note: Note) => MentionElement | null>();
  public readonly isAutomatedActionNoteFn = input.required<(note: Note) => boolean>();
  public readonly visibilitySelectorComponent = input<VisibilitySelector>();
  public readonly visibilityComponentInputs = input<Partial<VisibilitySelectorInputs>>();
  public readonly canReply = input(true);
  public readonly canPin = input(true);
  public readonly hasFileUpload = input(true);
  public readonly hasReactions = input(true);

  public readonly scrollToNoteId = input<number>(undefined!);

  public replyTypingStart = output<NoteReplyTypingStartEvent>();
  public replyTypingFinish = output<void>();

  public noteDiscardClick = output<NoteDiscardEvent>();
  public noteEditClick = output<NoteEditEvent>();
  public noteReplyClick = output<NoteReplyClickEvent>();
  public noteSaveClick = output<NoteSave<Note>>();
  public noteChecklistClick = output<{
    id: number;
    bodyJson: object;
  }>();
  public noteDeleteClick = output<SavedNoteId>();
  public notePinClick = output<SavedNoteId>();
  public noteUnpinClick = output<SavedNoteId>();
  public uploadAttachmentClick = output<NoteUploadAttachmentEvent>();
  public deleteAttachmentClick = output<NoteDeleteAttachmentEvent<Note>>();

  public cachedReply: NoteReplyChangesEvent | null = null;
  public sortedNotes: Note[];
  public allNotesFlat: Note[];

  @ViewChildren('noteRef', { read: ElementRef })
  private noteItems: QueryList<ElementRef>;

  private sortFunctions = {
    sortReplies: (a: Note, b: Note) => (a.createdAt > b.createdAt ? 1 : -1),
    sortMainNotes: (a: Note, b: Note) => {
      if (a.pinnedAt && !b.pinnedAt) return -1;
      if (!a.pinnedAt && b.pinnedAt) return 1;
      if (a.pinnedAt && b.pinnedAt) return a.pinnedAt > b.pinnedAt ? 1 : -1;

      return a.createdAt > b.createdAt ? -1 : 1;
    },
  };

  public ngAfterViewInit(): void {
    const noteScrollToElement = this.noteItems.find(
      noteItem =>
        noteItem && parseInt(noteItem.nativeElement.dataset.id, 10) === this.scrollToNoteId(),
    );
    if (noteScrollToElement) {
      noteScrollToElement.nativeElement.scrollIntoView();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.notes) {
      this.sortedNotes = this.notes()
        .map(note => ({
          ...note,
          replies: [...note.replies].sort(this.sortFunctions.sortReplies),
        }))
        .sort(this.sortFunctions.sortMainNotes);

      this.allNotesFlat = this.flattenNotes(this.sortedNotes);
    }
  }

  public setCachedReply({ replyToId, value }: NoteReplyChangesEvent): void {
    if (value === null) {
      this.cachedReply = null;
    } else {
      this.cachedReply = { replyToId, value };
    }
  }

  public trackByNote(index: number, note: Note): string | number {
    return note?.id || JSON.stringify(note);
  }

  public get typingAdmins(): UserBase[] {
    return this.currentlyTypedNotes()
      .filter(note => !note.replyToId)
      .map(note => note.admin);
  }

  private flattenNotes(notes: Note[]): Note[] {
    if (!notes || !notes.length) return [];

    return notes.flatMap(note => [
      { ...note, replies: [] },
      ...this.flattenNotes(<Note[]>note.replies),
    ]);
  }
}
